import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowRight, BookMarked, Award, Mic, RefreshCw, CheckCircle2 } from "lucide-react";
import LanguageHUD from "@/components/LanguageHUD";
import { CHARACTERS, CharacterById } from "@/components/LanguageCharacters";
import { useT } from "@/lib/i18n";

export default function LanguagesLanding() {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ profile: null, languages: [] });
  const [plan, setPlan] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(() => {
    return localStorage.getItem("lang_guide") || "owl";
  });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [langsR, onbR] = await Promise.all([
          api.get("/languages/"),
          api.get("/languages/onboarding/me").catch(() => ({ data: { has_plan: false, plan: null } })),
        ]);
        setData(langsR.data);
        if (!onbR.data.has_plan) {
          // first-time experience: redirect to onboarding
          navigate("/idiomas/onboarding", { replace: true });
          return;
        }
        setPlan(onbR.data.plan);
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudo cargar idiomas");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const pickCharacter = (id) => {
    setSelectedCharacter(id);
    localStorage.setItem("lang_guide", id);
    const name = CHARACTERS.find((c) => c.id === id).name;
    toast.success(t("common.success") + " " + name);
  };

  if (loading) {
    return <div className="text-[#374151]">{t("common.loading")}</div>;
  }

  return (
    <div className="space-y-8" data-testid="languages-landing">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1B2E7A] via-[#1B2E7A] to-[#13205A] text-white p-8 md:p-12">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #F4B81F 0, transparent 40%), radial-gradient(circle at 80% 70%, #DC2626 0, transparent 35%)",
        }} />
        <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.24em] text-[#F4B81F] mb-3 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> {t("lang.hero.eyebrow")}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
              {t("lang.hero.title")}
            </h1>
            <p className="text-[#E5E7EB] text-base md:text-lg mb-6">
              {t("lang.hero.subtitle")}
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                <BookMarked className="h-3.5 w-3.5" /> {t("lang.feature.lessons")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                <Mic className="h-3.5 w-3.5" /> {t("lang.feature.voice")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                🔥 {t("lang.feature.streaks")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                <Award className="h-3.5 w-3.5" /> {t("lang.feature.badges")}
              </span>
            </div>
          </div>
          <div className="flex justify-center md:justify-end">
            <CharacterById id={selectedCharacter} mood="cheer" size={180} />
          </div>
        </div>
      </div>

      {/* Profile HUD */}
      <LanguageHUD profile={data.profile} />

      {/* Personalized Plan Card */}
      {plan && (
        <section className="bg-gradient-to-br from-[#FEF3C7] to-[#FAEFD7] border border-[#F4B81F]/30 rounded-2xl p-5 md:p-6" data-testid="plan-card">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <CharacterById id={selectedCharacter} size={84} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#B45309] mb-1 inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> {t("lang.plan.eyebrow")}
              </p>
              <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] leading-tight">
                {plan.summary_es}
              </h2>
              {plan.coach_message_es && (
                <p className="text-sm text-[#854D0E] mt-2">{plan.coach_message_es}</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <Tile label={t("lang.plan.minutes_per_week")} value={plan.weekly_minutes_goal} />
                <Tile label={t("lang.plan.lessons_per_week")} value={plan.weekly_lessons_goal} />
                <Tile label={t("lang.plan.next_lessons")} value={plan.first_lessons?.length || 0} />
                <Tile label={t("lang.plan.scenario")} value="✨" subtle={t("lang.plan.scenario_value")} />
              </div>
              {plan.milestones_es?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {plan.milestones_es.slice(0, 3).map((m, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-white border border-[#F4B81F]/30 text-[#854D0E]">
                      <CheckCircle2 className="h-3 w-3 text-[#1B2E7A]" /> {m}
                    </span>
                  ))}
                </div>
              )}
              <button
                onClick={() => navigate("/idiomas/onboarding")}
                className="mt-4 text-xs text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1"
                data-testid="reconfigure-plan"
              >
                <RefreshCw className="h-3.5 w-3.5" /> {t("lang.plan.reconfigure")}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Character picker */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{t("lang.section.guide.eyebrow")}</p>
            <h2 className="font-heading text-2xl text-[#1A1A1A]">{t("lang.section.guide.title")}</h2>
          </div>
          <p className="text-xs text-[#6B7280]">{t("lang.section.guide.note")}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CHARACTERS.map((c) => {
            const Comp = c.component;
            const isActive = c.id === selectedCharacter;
            return (
              <button
                key={c.id}
                onClick={() => pickCharacter(c.id)}
                className={`text-left rounded-2xl border transition-all duration-200 p-4 hover:-translate-y-0.5 ${
                  isActive
                    ? "border-[#1B2E7A] bg-white shadow-md ring-2 ring-[#1B2E7A]/20"
                    : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
                }`}
                data-testid={`pick-character-${c.id}`}
              >
                <div className="flex justify-center">
                  <Comp size={110} mood={isActive ? "cheer" : "idle"} />
                </div>
                <p className="font-heading text-lg text-[#1A1A1A] mt-2">{c.name}</p>
                <p className="text-xs text-[#6B7280] leading-snug">{c.tagline}</p>
                {isActive && (
                  <span className="inline-block mt-2 text-[10px] uppercase tracking-[0.18em] text-[#1B2E7A] bg-[#1B2E7A]/10 px-2 py-0.5 rounded">
                    {t("lang.character.active")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Language list */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{t("lang.section.catalog.eyebrow")}</p>
            <h2 className="font-heading text-2xl text-[#1A1A1A]">{t("lang.section.catalog.title")}</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.languages.map((lang) => {
            const pct = lang.total_lessons
              ? Math.round((lang.completed_lessons / lang.total_lessons) * 100)
              : 0;
            return (
              <button
                key={lang.code}
                onClick={() => navigate(`/idiomas/${lang.code}`)}
                className="text-left bg-white rounded-2xl border border-[#E5E7EB] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-200 group"
                data-testid={`lang-card-${lang.code}`}
                style={{ borderTop: `4px solid ${lang.color}` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-5xl" aria-hidden>{lang.flag}</span>
                  <ArrowRight className="h-5 w-5 text-[#6B7280] group-hover:text-[#1B2E7A] group-hover:translate-x-1 transition-all" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{lang.native_name}</p>
                <h3 className="font-heading text-2xl text-[#1A1A1A] mb-2">{lang.name}</h3>
                <p className="text-sm text-[#6B7280] mb-4 leading-snug">{lang.description}</p>
                <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1.5">
                  <span>{t("lang.lessons_progress", { completed: lang.completed_lessons, total: lang.total_lessons })}</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: lang.color }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function Tile({ label, value, subtle }) {
  return (
    <div className="bg-white border border-[#F4B81F]/30 rounded-xl p-3">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{label}</p>
      <p className="font-heading text-xl text-[#1A1A1A] leading-tight">{value}</p>
      {subtle && <p className="text-[10px] text-[#6B7280]">{subtle}</p>}
    </div>
  );
}
